/**
 * Project Service
 * Handles project management and relationships
 */

import { Project, ProjectFormData, Status } from '@/types'
import { BaseService } from './base.service'
import { StorageKeys } from './storage'
import { cleanUser, cleanUsers, cleanWorkspace } from '@/lib/utils/clean-references'

class ProjectService extends BaseService<Project> {
  constructor() {
    super(StorageKeys.PROJECTS)
  }

  /**
   * Create project with creator and workspace
   */
  async createProject(data: ProjectFormData, creatorId: string): Promise<Project> {
    const { userService } = await import('./user.service')
    const { workspaceService } = await import('./workspace.service')

    const creator = await userService.getById(creatorId)
    if (!creator) {
      throw new Error('Creator not found')
    }

    const workspace = await workspaceService.getById(data.workspaceId)
    if (!workspace) {
      throw new Error('Workspace not found')
    }

    const users = data.userIds ? await userService.getByIds(data.userIds) : [creator]

    // Clean all references to prevent circular reference issues
    return this.create({
      creator: cleanUser(creator),
      name: data.name,
      description: data.description,
      status: data.status,
      priority: data.priority,
      horizon: data.horizon,
      workspace: cleanWorkspace(workspace),
      users: cleanUsers(users),
      tasks: [],
      messages: [],
      documents: [],
    })
  }

  /**
   * Get projects for workspace
   */
  async getProjectsForWorkspace(workspaceId: string): Promise<Project[]> {
    return this.filter((project) => project.workspace.id === workspaceId)
  }

  /**
   * Get projects for user
   */
  async getProjectsForUser(userId: string): Promise<Project[]> {
    return this.filter((project) => project.users.some((u) => u.id === userId))
  }

  /**
   * Get projects by status
   */
  async getProjectsByStatus(status: Status): Promise<Project[]> {
    return this.filter((project) => project.status === status)
  }

  /**
   * Add user to project
   */
  async addUser(projectId: string, userId: string): Promise<Project> {
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }

    const { userService } = await import('./user.service')
    const user = await userService.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    if (project.users.some((u) => u.id === userId)) {
      throw new Error('User already in project')
    }

    // Clean user to prevent circular reference issues
    return this.update(projectId, {
      users: [...project.users, cleanUser(user)],
    } as Partial<Project>)
  }

  /**
   * Remove user from project
   */
  async removeUser(projectId: string, userId: string): Promise<Project> {
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }

    return this.update(projectId, {
      users: project.users.filter((u) => u.id !== userId),
    } as Partial<Project>)
  }

  /**
   * Update project status
   */
  async updateStatus(projectId: string, status: Status): Promise<Project> {
    return this.update(projectId, { status } as Partial<Project>)
  }

  /**
   * Search projects
   */
  async searchProjects(query: string): Promise<Project[]> {
    return this.search(query, ['name', 'description'])
  }

  /**
   * Get project by ID with populated messages and documents
   * This ensures messages and documents are always loaded from their respective storage
   */
  async getByIdWithMessages(projectId: string): Promise<Project | null> {
    const project = await this.getById(projectId)
    if (!project) return null

    // Get messages from message storage
    const { messageService } = await import('./message.service')
    const allMessages = await messageService.getAll()
    const projectMessages = allMessages.filter((message) =>
      message.projects.some((p) => p.id === projectId)
    )

    // Get documents from document storage
    const { documentService } = await import('./document.service')
    const allDocuments = await documentService.getAll()
    const projectDocuments = allDocuments.filter((document) =>
      document.projects.some((p) => p.id === projectId)
    )

    return {
      ...project,
      messages: projectMessages || [],
      documents: projectDocuments || [],
    }
  }

  /**
   * Get project stats
   */
  async getStats(projectId: string): Promise<{
    totalTasks: number
    completedTasks: number
    totalMessages: number
    totalDocuments: number
  }> {
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }

    return {
      totalTasks: project.tasks.length,
      completedTasks: project.tasks.filter((t) => t.status === Status.Done).length,
      totalMessages: project.messages.length,
      totalDocuments: project.documents.length,
    }
  }
}

export const projectService = new ProjectService()
